import React, { FunctionComponent, ReactText, useEffect, useState } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';

import {
  AppBar,
  Button,
  ButtonProps as ButtonComponentProps,
  Drawer,
  Link,
  NavItem,
  NavItems,
  RaisinLogo,
  TertiaryButton
} from '@savebetter/components';
import { snowplow } from '@savebetter/tracking-events';

import customConfig from '../../../config';

import { LinkProps } from './Link';
import { MediaProps } from './Media';

interface ButtonProps {
  config?: ButtonComponentProps;
  value: ReactText;
}

export interface FooterButtonsProps {
  primaryButton?: ButtonProps;
  secondaryButton?: ButtonProps;
}

interface NavItemType {
  label: string;
  url: string;
  active: boolean;
  withIcon?: boolean;
  iconUrl?: string;
  iconBg?: string;
}
interface NestedNavItemType {
  label: string;
  url: string;
  active: boolean;
  navigationElements?: NavItemType[];
  withIcon?: boolean;
  iconUrl?: string;
  iconBg?: string;
}

export interface MobileNavigationProps {
  className?: string;
  navItems: Array<NestedNavItemType>;
  location: string;
}

interface NavigationElement extends SbBlokData {
  link: {
    label: string;
    cached_url: string;
  };
  navigationElements: NavigationElement[];
  label: string;
  withIcon: boolean;
  icon: {
    filename?: string;
  };
  iconBackgroundColor?: string;
}

interface Blok extends SbBlokData {
  logoImage: MediaProps[];
  primaryButton?: LinkProps[];
  secondaryButton?: LinkProps[];
  navigationElements: NavigationElement[];
}

const buttonClassNames = (kind: 'primary' | 'secondary' = 'primary'): string =>
  classnames('w-full md:w-auto', {
    ['border-none hover:bg-black-40 text-black hover:text-black font-[400]']: kind === 'secondary'
  });

const FooterButtons: FunctionComponent = () => (
  <div className="flex flex-col lg:flex-row gap-2 mt-8 md:mt-0 lg:m-0 items-center px-4 lg:px-0">
    <Button
      href={`${customConfig.postLoginUrl}/auth/login`}
      size="small"
      kind="secondary"
      className={buttonClassNames('secondary')}
      target="_blank"
    >
      Log In
    </Button>
    <Button
      onClick={() => {
        snowplow.trackEvent(snowplow.EVENTS.GET_STARTED_CLICK);
        window.open(`${customConfig.postLoginUrl}/auth/register`, '_blank');
      }}
      size="small"
      className={buttonClassNames()}
      target="_blank"
    >
      Get Started
    </Button>
  </div>
);

const prepareNavLink = (links: NavigationElement[], location: string): NavItemType[] => {
  const preparedLinks = [];
  for (const link of links) {
    const isExternalLink = link.link.cached_url.includes('https://');
    preparedLinks.push({
      label: link.label,
      url: isExternalLink ? link.link.cached_url : '/' + link.link.cached_url,
      active: location === `/${link.link.cached_url}`,
      withIcon: link.withIcon,
      iconUrl: link.icon?.filename,
      iconBg: link.iconBackgroundColor
    });
  }
  return preparedLinks;
};
const prepareNavLinks = (sblinks: NavigationElement[], location: string): NestedNavItemType[] => {
  const preparedLinks = [];

  for (const link of sblinks) {
    const isExternalLink = link.link.cached_url.includes('https://');
    const navigationUrls: string[] = link.navigationElements.map(obj => '/' + obj.link.cached_url);
    navigationUrls.push('/' + link.link.cached_url); // all navigation url in that nav tree.
    preparedLinks.push({
      label: link.label,
      url: isExternalLink ? link.link.cached_url : '/' + link.link.cached_url,
      // highlight/active the parent navItem if any element from the tree is selected.
      active: navigationUrls.includes(location),
      navigationElements: prepareNavLink(link.navigationElements, location),
      withIcon: link.withIcon,
      iconUrl: link.icon?.filename,
      iconBg: link.iconBackgroundColor
    });
  }
  return preparedLinks;
};

const MobileNavigation: FunctionComponent<React.PropsWithChildren<MobileNavigationProps>> = ({
  navItems,
  location
}) => {
  const [drawerVisibility, setDrawerVisibility] = useState<boolean>(false);

  return (
    <>
      <TertiaryButton
        title="Close button"
        icon="Hamburger"
        onClick={(): void => setDrawerVisibility(true)}
        data-testid="drawer-hamburger"
        aria-label="Open the navigation"
      />
      <Drawer
        position="right"
        buttonClassName="mt-0"
        onClose={(): void => setDrawerVisibility(false)}
        open={drawerVisibility}
        logo={
          <>
            <RaisinLogo className="block md:hidden" />
            <NavItem
              className="hidden md:block -ml-5"
              description="Home"
              href="/"
              active={location === '/'}
              aria-label="Main"
              withIcon={false}
            />
          </>
        }
      >
        <div className="flex flex-col">
          {navItems.length > 0 && (
            <>
              <NavItems // showing in mobile
                className="pb-4 md:hidden "
                items={[{ label: 'Home', url: '/', active: location === '/', withIcon: false }, ...navItems]}
                aria-label="Main"
              />

              <NavItems // showing in medium device
                className="pb-4 hidden md:block "
                items={navItems}
                aria-label="Main"
              />
            </>
          )}
          <FooterButtons />
        </div>
      </Drawer>
    </>
  );
};

export const NavigationV1: FunctionComponent<React.PropsWithChildren<{ blok: Blok }>> = ({ blok }) => {
  const [location, setLocation] = useState('');
  const { navigationElements = [] } = blok;
  const isMobileApp = typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true';

  useEffect(() => {
    if (window) {
      setLocation(window.location.pathname);
    }
  }, []);

  if (isMobileApp) {
    return null;
  }

  return (
    <AppBar
      {...storyblokEditable(blok)}
      className="lg:pb-0 lg:px-0 md:pl-0 md:pr-0 sm:px-0 align-center box-border lg:justify-start"
    >
      <Link href="/" className="self-center" borderWidth="0">
        <RaisinLogo />
      </Link>
      <div className="hidden lg:flex flex-row items-center justify-between w-full">
        <NavItems
          className="ml-8"
          data-testid="navigation"
          aria-label="Main"
          items={prepareNavLinks(navigationElements, location)}
        />
        <FooterButtons />
      </div>
      <div className="lg:hidden flex items-center">
        <MobileNavigation navItems={prepareNavLinks(navigationElements, location)} location={location} />
      </div>
    </AppBar>
  );
};
